/**
 * Pure candidate <-> job scoring. No I/O, no randomness.
 *
 * Two methods share one output shape (`MatchResult`) and record which one produced the score:
 *  - 'embeddings': semantic factor = cosine of embedding vectors (computed by the API layer via Workers AI),
 *  - 'lexical'   : semantic factor = TF-IDF cosine over the two texts (this file; needs no model).
 * Both blend the semantic factor with skills coverage and a location/salary "fit" factor.
 * Factors that cannot be evaluated (unknown location, no salary data...) are EXCLUDED and the remaining
 * weights renormalised — an unknown is never scored as good or bad.
 */

export type MatchMethod = 'embeddings' | 'lexical';
export type RemoteMode = 'onsite' | 'hybrid' | 'remote';

export interface CandidateDoc {
  headline: string;
  about: string;
  skills: string[];
  /** Latest job titles, most recent first. */
  titles: string[];
  location: string;
  /** Minimum expected annual salary in cents, when the candidate stated one. */
  expectedSalaryMin?: number | null;
  currency?: string | null;
}

export interface JobDoc {
  title: string;
  description: string;
  skills: string[];
  location: string;
  remote: RemoteMode;
  /** Cents per year. */
  salaryMin: number | null;
  salaryMax: number | null;
  currency: string;
}

export interface Factors {
  semantic: number | null;
  skills: number | null;
  fit: number | null;
}

export interface MatchResult {
  /** Integer 0..100. */
  score: number;
  method: MatchMethod;
  factors: Factors;
  evidence: { matchedSkills: string[]; missingSkills: string[] };
  /** Human-readable explanation, one sentence per factor. */
  reasons: string[];
}

/** Configuration constants (spec R-073: weights live in shared). Each set sums to 1. */
export const MATCH_WEIGHTS = {
  embeddings: { semantic: 0.55, skills: 0.25, fit: 0.2 },
  lexical: { semantic: 0.35, skills: 0.4, fit: 0.25 },
} as const satisfies Record<MatchMethod, Record<keyof Factors, number>>;

const STOPWORDS = new Set(
  (
    'a an and are as at be but by for from has have in into is it its of on or our that the their ' +
    'this to we will with you your i my me about not can'
  ).split(' '),
);

export function tokenize(text: string): string[] {
  const out: string[] = [];
  for (const m of text.toLowerCase().matchAll(/[a-z0-9][a-z0-9+#.]*/g)) {
    const t = m[0].replace(/\.+$/, '');
    if (t && !STOPWORDS.has(t)) out.push(t);
  }
  return out;
}

export const normalizeSkill = (s: string): string => s.trim().toLowerCase();

const uniq = <T>(xs: T[]): T[] => [...new Set(xs)];
const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));

export function candidateText(c: CandidateDoc): string {
  return [c.headline, c.about, c.skills.join(' '), c.titles.join(' ')].filter(Boolean).join('\n');
}

export function jobText(j: JobDoc): string {
  return [j.title, j.description, j.skills.join(' ')].filter(Boolean).join('\n');
}

/** cos(a,b) = a·b / (|a||b|). Throws on empty or length-mismatched input; a zero vector yields 0. */
export function cosine(a: readonly number[], b: readonly number[]): number {
  if (a.length === 0 || b.length === 0) throw new RangeError('cosine: empty vector');
  if (a.length !== b.length)
    throw new RangeError(`cosine: length mismatch (${a.length} vs ${b.length})`);
  let dot = 0;
  let na = 0;
  let nb = 0;
  for (let i = 0; i < a.length; i++) {
    const x = a[i]!;
    const y = b[i]!;
    dot += x * y;
    na += x * x;
    nb += y * y;
  }
  if (na === 0 || nb === 0) return 0;
  return dot / (Math.sqrt(na) * Math.sqrt(nb));
}

/** |A∩B| / |A∪B| over normalised tokens; null when both sets are empty. */
export function jaccard(a: readonly string[], b: readonly string[]): number | null {
  const A = new Set(a.map(normalizeSkill).filter(Boolean));
  const B = new Set(b.map(normalizeSkill).filter(Boolean));
  const union = new Set([...A, ...B]);
  if (union.size === 0) return null;
  let inter = 0;
  for (const x of A) if (B.has(x)) inter++;
  return inter / union.size;
}

export interface SkillsMatch {
  matched: string[];
  missing: string[];
  /** Fraction of the JOB's skills the candidate has; null when the job lists none. */
  coverage: number | null;
  jaccard: number | null;
}

export function skillsMatch(
  candidateSkills: readonly string[],
  jobSkills: readonly string[],
): SkillsMatch {
  const have = new Set(candidateSkills.map(normalizeSkill).filter(Boolean));
  const need = uniq(jobSkills.map(normalizeSkill).filter(Boolean));
  const matched = need.filter((s) => have.has(s));
  const missing = need.filter((s) => !have.has(s));
  return {
    matched,
    missing,
    coverage: need.length === 0 ? null : matched.length / need.length,
    jaccard: jaccard(candidateSkills, jobSkills),
  };
}

/** Smoothed inverse document frequency: ln((N+1)/(df+1)) + 1, over token lists. */
export function buildIdf(docs: readonly (readonly string[])[]): Map<string, number> {
  const df = new Map<string, number>();
  for (const d of docs) for (const t of new Set(d)) df.set(t, (df.get(t) ?? 0) + 1);
  const n = docs.length;
  const idf = new Map<string, number>();
  for (const [t, c] of df) idf.set(t, Math.log((n + 1) / (c + 1)) + 1);
  return idf;
}

/** Cosine of TF-IDF vectors. Terms absent from `idf` get weight 1 (neutral). */
export function tfidfCosine(
  a: readonly string[],
  b: readonly string[],
  idf: ReadonlyMap<string, number>,
): number {
  const vec = (toks: readonly string[]) => {
    const tf = new Map<string, number>();
    for (const t of toks) tf.set(t, (tf.get(t) ?? 0) + 1);
    const v = new Map<string, number>();
    for (const [t, c] of tf) v.set(t, c * (idf.get(t) ?? 1));
    return v;
  };
  const va = vec(a);
  const vb = vec(b);
  let dot = 0;
  let na = 0;
  let nb = 0;
  for (const [t, w] of va) {
    na += w * w;
    const o = vb.get(t);
    if (o !== undefined) dot += w * o;
  }
  for (const w of vb.values()) nb += w * w;
  if (na === 0 || nb === 0) return 0;
  return dot / (Math.sqrt(na) * Math.sqrt(nb));
}

export interface FitPart {
  score: number | null;
  reason: string;
}

const parts = (loc: string): string[] =>
  loc
    .toLowerCase()
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);

export function locationFit(c: CandidateDoc, j: JobDoc): FitPart {
  if (j.remote === 'remote')
    return { score: 1, reason: 'Job is fully remote, so location is not a constraint.' };
  const cp = parts(c.location);
  const jp = parts(j.location);
  if (cp.length === 0 || jp.length === 0)
    return { score: null, reason: 'Location not compared: candidate or job location is not set.' };
  if (cp[0] === jp[0]) return { score: 1, reason: `Location matches (${j.location.trim()}).` };
  if (cp.some((p) => jp.includes(p)))
    return {
      score: 0.6,
      reason: `Same region but different city (candidate: ${c.location.trim()}; job: ${j.location.trim()}).`,
    };
  if (j.remote === 'hybrid')
    return {
      score: 0.2,
      reason: `Hybrid job in ${j.location.trim()}; candidate is in ${c.location.trim()}.`,
    };
  return {
    score: 0,
    reason: `On-site job in ${j.location.trim()}; candidate is in ${c.location.trim()}.`,
  };
}

export function salaryFit(c: CandidateDoc, j: JobDoc): FitPart {
  const expected = c.expectedSalaryMin ?? null;
  const top = j.salaryMax ?? j.salaryMin;
  if (expected === null || expected <= 0 || top === null)
    return { score: null, reason: 'Salary not compared: expectation or job salary is not set.' };
  if ((c.currency ?? j.currency) !== j.currency)
    return { score: null, reason: 'Salary not compared: currencies differ.' };
  const s = Math.min(1, top / expected);
  return {
    score: s,
    reason:
      s >= 1
        ? 'Job salary range meets the stated expectation.'
        : `Top of the job salary range is ${Math.round(s * 100)}% of the stated expectation.`,
  };
}

/** Location and salary combined into one "fit" factor (mean of the parts that could be evaluated). */
function fitFactor(c: CandidateDoc, j: JobDoc): { score: number | null; reasons: string[] } {
  const l = locationFit(c, j);
  const s = salaryFit(c, j);
  const vals = [l.score, s.score].filter((x): x is number => x !== null);
  return {
    score: vals.length === 0 ? null : vals.reduce((a, b) => a + b, 0) / vals.length,
    reasons: [l.reason, s.reason],
  };
}

/** Weighted blend, renormalised over evaluable factors, rounded to an integer 0..100. */
export function combineScore(method: MatchMethod, f: Factors): number {
  const w = MATCH_WEIGHTS[method];
  let num = 0;
  let den = 0;
  for (const k of ['semantic', 'skills', 'fit'] as const) {
    const v = f[k];
    if (v === null) continue;
    num += w[k] * clamp01(v);
    den += w[k];
  }
  return den === 0 ? 0 : Math.round((100 * num) / den);
}

const pct = (n: number): string => `${Math.round(clamp01(n) * 100)}%`;

/** Build the result from an already computed semantic factor (cosine of embeddings, or TF-IDF cosine). */
export function matchWithSemantic(
  method: MatchMethod,
  semantic: number,
  c: CandidateDoc,
  j: JobDoc,
): MatchResult {
  const sk = skillsMatch(c.skills, j.skills);
  const fit = fitFactor(c, j);
  const factors: Factors = { semantic: clamp01(semantic), skills: sk.coverage, fit: fit.score };
  const reasons: string[] = [
    method === 'embeddings'
      ? `Semantic similarity between profile and job description is ${pct(semantic)} (embeddings).`
      : `Text similarity between profile and job description is ${pct(semantic)} (TF-IDF).`,
  ];
  const total = sk.matched.length + sk.missing.length;
  if (total === 0) reasons.push('Job lists no required skills, so skills were not scored.');
  else {
    reasons.push(
      `Has ${sk.matched.length} of ${total} required skills` +
        (sk.matched.length ? `: ${sk.matched.join(', ')}.` : '.'),
    );
    if (sk.missing.length) reasons.push(`Missing skills: ${sk.missing.join(', ')}.`);
  }
  reasons.push(...fit.reasons);
  return {
    score: combineScore(method, factors),
    method,
    factors,
    evidence: { matchedSkills: sk.matched, missingSkills: sk.missing },
    reasons,
  };
}

/** Lexical scorer for one pair. `idf` should be built over the whole batch being ranked. */
export function scoreLexical(
  c: CandidateDoc,
  j: JobDoc,
  idf: ReadonlyMap<string, number>,
): MatchResult {
  const sem = tfidfCosine(tokenize(candidateText(c)), tokenize(jobText(j)), idf);
  return matchWithSemantic('lexical', sem, c, j);
}
